import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LoadingSpinner, useToast } from '@examflow/ui';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { bankService } from '../lib/bankService';
import {
  api,
  type AIGenerationSourceMetadata,
} from '../lib/api';
import { useAuth } from '../features/auth/AuthContext';
import {
  buildLearnerContentAccessGate,
  buildLearnerAccessibleBankIdSet,
  isPracticeAttempt,
  resolveLearnerTrainingSectionId,
  resolveQuizLaunchGate,
} from '@examflow/learning-space';
import { getNote, setNote } from '../data/local/questionNotes';
import { AlertTriangle, BookOpen } from 'lucide-react';


const ENTITLEMENT_EXPIRED_MESSAGE = '您的训练权限已到期，请兑换授权后继续使用。';
import type { ExamTypeConfig, LocalBank, Question, Paper } from '@examflow/core';
import {
  TimerManager,
  ScoreCalculator,
  buildPlatformScope,
  buildPaper,
  flattenPaper,
  buildAnswerSheetBySection,
  isAnswerCorrect,
  questionShowsAudioStimulus,
} from '@examflow/core';
import { isLevelSupportedForExam, loadExamConfig } from '@examflow/exams';
import { examSessionRepository, writingDraftStore, type ActiveSession } from '../data/local';
import {
  QuestionHeader,
  NavigationBar,
  AnswerSheet,
  QuizModals,
  QuizResult,
  QuestionRenderer,
  ReadingPassageBlock,
  QuestionFeedbackButton,
  QuestionFeedbackModal,
  type QuestionFeedbackData,
} from '../components/quiz';
import { buildQuizViewModel } from '../features/quiz/quizViewModel';
import {
  buildQuizTrainingAttemptContextFromAttempt,
  resolveQuizActiveExamType,
} from '../features/quiz/quizTrainingContext';
import {
  assemblePracticeQuestionList,
  type PracticePaperAssemblyResult,
} from '../features/quiz/practicePaperAssembly';
import {
  abandonQuizSession,
  completeQuizSession,
  createExamSession,
  createPracticeSession,
  findIncompleteExamSession,
  findIncompletePracticeSession,
  resolveIncompletePracticeDisposition,
  hydrateQuizSession,
  readQuizSessionId,
  updateQuizSessionProgress,
  withQuizSessionId,
  withoutQuizSessionId,
  type PracticeLaunchFingerprint,
} from '../features/quiz/quizSessionRuntime';
import {
  applyCachedPlayableAudio,
  prefetchQuestionsAudio,
} from '../features/quiz/resolveQuestionAudio';
import {
  buildTrainingAttemptPersistPayload,
  type SubmittedQuizAnswer,
} from '../features/quiz/trainingAttemptPersist';
import { WritingSessionChrome } from '../features/quiz/WritingSessionChrome';
import { WritingSessionPanel } from '../features/quiz/WritingSessionPanel';
import { WritingHistoryPanel } from '../features/quiz/WritingHistoryPanel';
import { resolveWritingDisplayLabels } from '../features/quiz/openEndedWriting';
import { resolveAttemptExitHref } from '../features/quiz/attemptExit';
import { attemptEvidenceClient } from '../features/quiz/attemptEvidenceClient';
import { useAchievements } from '../hooks';

function resolveQuestionsByIds(bank: LocalBank, questionIds: string[]): Question[] {
  const questionMap = new Map((bank.questions || []).map((q) => [q.id, q]));
  return questionIds
    .map((id) => questionMap.get(id))
    .filter((q): q is Question => q != null);
}

function practicePaperFromPinnedQuestions(
  questions: Question[],
  sectionId: string | null
): PracticePaperAssemblyResult {
  const openEnded = sectionId === 'writing';
  return {
    questions,
    openResponseOnly: false,
    sessionResponseMode: openEnded ? 'open_ended' : 'closed',
    rawCount: questions.length,
    usableChoiceCount: openEnded ? 0 : questions.length,
    usableOpenEndedCount: openEnded ? questions.length : 0,
  };
}

type QuizMode = 'practice' | 'exam';
type QuestionSourceTrace = {
  bankId?: string | null;
  bankName?: string | null;
  sourceType?: string | null;
  sourceTaskId?: string | null;
  sourceMetadata?: AIGenerationSourceMetadata | null;
};
type SectionedQuestion = Question & QuestionSourceTrace & { section?: string };
type ExamSectionConfig = { duration: number };

function QuizPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const { checkAndUnlockAchievements } = useAchievements();
  const bankId = searchParams.get('bankId');
  const languageIdParam = searchParams.get('languageId');
  const examTypeParam = searchParams.get('examType');
  const sectionId = searchParams.get('sectionId');
  const trainingPackParam = searchParams.get('trainingPack');
  const trainingModuleParam = searchParams.get('trainingModule');
  const reviewFocusParam = searchParams.get('reviewFocus');
  const modeParam = searchParams.get('mode');
  const launchGate = useMemo(() => resolveQuizLaunchGate(searchParams), [searchParams]);
  // Gate is the sole runtime authority once ready (no raw-query dual track).
  const mode: QuizMode =
    launchGate.status === 'ready'
      ? launchGate.mode
      : modeParam === 'exam'
        ? 'exam'
        : 'practice';
  const launchAttempt = launchGate.status === 'ready' ? launchGate.attempt : null;
  const practiceAttempt =
    launchAttempt && isPracticeAttempt(launchAttempt) ? launchAttempt : null;
  const questionCount: number | 'all' = practiceAttempt
    ? practiceAttempt.questionCount
    : (() => {
        const questionCountParam = searchParams.get('questionCount');
        if (!questionCountParam) return 'all';
        return questionCountParam === 'all' ? 'all' : parseInt(questionCountParam, 10);
      })();
  const activeExamType = useMemo(() => {
    if (launchGate.status === 'ready') {
      return launchGate.examType;
    }
    // No preference fallback: multilingual isolation requires an explicit PlatformScope.
    return resolveQuizActiveExamType({
      languageId: languageIdParam,
      examType: examTypeParam,
      trainingPack: trainingPackParam,
      fallbackExamType: null,
    });
  }, [examTypeParam, languageIdParam, launchGate, trainingPackParam]);
  // Explicit AttemptSpec / ?level= only — never silent localStorage filtering.
  const difficultyFilter = useMemo((): string | null => {
    const examType = activeExamType || examTypeParam || trainingPackParam;
    if (!examType) return null;
    const fromAttempt = practiceAttempt?.level?.trim();
    if (fromAttempt && isLevelSupportedForExam(examType, fromAttempt)) {
      return fromAttempt;
    }
    const fromQuery = searchParams.get('level')?.trim();
    if (fromQuery && isLevelSupportedForExam(examType, fromQuery)) {
      return fromQuery;
    }
    return null;
  }, [activeExamType, examTypeParam, practiceAttempt, searchParams, trainingPackParam]);
  const writingTaskNumberFilter = useMemo((): number | null => {
    if (practiceAttempt?.kind === 'training' && practiceAttempt.taskNumber != null) {
      return practiceAttempt.taskNumber;
    }
    const raw = searchParams.get('taskNumber');
    if (!raw) return null;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? Math.trunc(num) : null;
  }, [practiceAttempt, searchParams]);
  const writingTopicIdFilter = useMemo((): string | null => {
    if (practiceAttempt?.kind === 'training' && practiceAttempt.topicId) {
      return practiceAttempt.topicId;
    }
    const raw = searchParams.get('topicId')?.trim().toLowerCase();
    return raw || null;
  }, [practiceAttempt, searchParams]);
  const resumeQuestionId = useMemo((): string | null => {
    const raw = searchParams.get('resumeQuestionId')?.trim();
    return raw || null;
  }, [searchParams]);
  const trainingAttemptContext = useMemo(() => {
    if (!practiceAttempt) return null;
    return buildQuizTrainingAttemptContextFromAttempt(practiceAttempt);
  }, [practiceAttempt]);
  const effectiveTrainingSectionId = useMemo(() => {
    if (practiceAttempt) {
      if (practiceAttempt.kind === 'diagnosis') {
        return null;
      }
      return resolveLearnerTrainingSectionId({
        sectionId: practiceAttempt.skillId,
        reviewFocus: practiceAttempt.reviewFocus ?? null,
        trainingModule: practiceAttempt.skillId,
      });
    }
    return resolveLearnerTrainingSectionId({
      sectionId,
      reviewFocus: reviewFocusParam,
      trainingModule: trainingModuleParam,
    });
  }, [practiceAttempt, reviewFocusParam, sectionId, trainingModuleParam]);

  const urlSessionId = useMemo(() => readQuizSessionId(searchParams), [searchParams]);

  const practiceLaunchFingerprint = useMemo((): PracticeLaunchFingerprint | null => {
    if (mode !== 'practice' || !activeExamType) return null;
    const languageId =
      launchGate.status === 'ready'
        ? launchGate.languageId
        : languageIdParam?.trim() || null;
    return {
      examType: activeExamType,
      languageId,
      sectionId: effectiveTrainingSectionId,
      difficultyFilter,
      taskNumber: writingTaskNumberFilter,
      topicId: writingTopicIdFilter,
      intensity: practiceAttempt?.intensity ?? null,
      questionCount:
        typeof questionCount === 'number' || questionCount === 'all' ? questionCount : null,
    };
  }, [
    mode,
    activeExamType,
    launchGate,
    languageIdParam,
    effectiveTrainingSectionId,
    difficultyFilter,
    writingTaskNumberFilter,
    writingTopicIdFilter,
    practiceAttempt?.intensity,
    questionCount,
  ]);

  const [localBank, setLocalBank] = useState<LocalBank | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userSelections, setUserSelections] = useState<Record<string, string>>({});
  const [showAnalysisByQuestionId, setShowAnalysisByQuestionId] = useState<Record<string, boolean>>(
    {}
  );
  const [answers, setAnswers] = useState<SubmittedQuizAnswer[]>([]);
  const [quizFinished, setQuizFinished] = useState(false);
  const [sectionName, setSectionName] = useState<string | null>(null);
  const [markedForReview, setMarkedForReview] = useState<Set<number>>(new Set());
  const [noteText, setNoteText] = useState<string>('');

  const [timer, setTimer] = useState<TimerManager | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  /** Fixed Mock budget for SessionClock remaining ring (not mutated by ticks). */
  const [examDurationMs, setExamDurationMs] = useState<number>(0);
  const [examConfig, setExamConfig] = useState<ExamTypeConfig | null>(null);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [questionSourceByQuestionId, setQuestionSourceByQuestionId] = useState<Record<
    string,
    QuestionSourceTrace
  > | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  // Resume UX lives only in QuizRuntimeLaunchProvider. QuizPage: hydrate(sessionId) | create.
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [restoredSectionBoundaries, setRestoredSectionBoundaries] = useState<
    { sectionId: string; sectionName: string; startIndex: number; endIndex: number }[] | null
  >(null);
  const [restoredQuestions, setRestoredQuestions] = useState<Question[] | null>(null);
  const [forceExamBuild, setForceExamBuild] = useState(0);
  /**
   * Inventory load key — must ignore sessionId. Writing sessionId after create used to
   * recreate launchGate → re-run loadBank → setIsLoading(true) over live questions (flash).
   */
  const bankLoadIdentity = useMemo(() => {
    if (launchGate.status === 'blocked') {
      return `blocked:${launchGate.reason}:${launchGate.message}`;
    }
    return [
      'ready',
      launchGate.mode,
      launchGate.examType,
      launchGate.languageId,
      bankId ?? '',
      effectiveTrainingSectionId ?? '',
      String(forceExamBuild),
    ].join('|');
  }, [launchGate, bankId, effectiveTrainingSectionId, forceExamBuild]);
  /** Pinned Training paper — only set by Session Runtime create/hydrate (never re-assemble on F5). */
  const [pinnedPracticePaper, setPinnedPracticePaper] = useState<PracticePaperAssemblyResult | null>(
    null
  );
  /** Frozen paper for QuizResult — survives session clear / boot races. */
  const [finishedPracticePaper, setFinishedPracticePaper] =
    useState<PracticePaperAssemblyResult | null>(null);
  const [forcePracticeCreate, setForcePracticeCreate] = useState(0);
  const [practiceSessionBooting, setPracticeSessionBooting] = useState(false);
  const [feedbackQuestionId, setFeedbackQuestionId] = useState<string | null>(null);
  const [writingDrafts, setWritingDrafts] = useState<Record<string, string>>({});
  const [writingDraftSavedAt, setWritingDraftSavedAt] = useState<Record<string, number | null>>(
    {}
  );
  const [writingDraftSavePending, setWritingDraftSavePending] = useState(false);
  const [writingElapsedSeconds, setWritingElapsedSeconds] = useState(0);
  const [writingClockPaused, setWritingClockPaused] = useState(false);
  const [showWritingHistory, setShowWritingHistory] = useState(false);
  const [practiceElapsedMs, setPracticeElapsedMs] = useState(0);
  const [practiceClockPaused, setPracticeClockPaused] = useState(false);
  const practiceStartedAtRef = useRef<number | null>(null);
  const practiceElapsedMsRef = useRef(0);
  const quizFinishedRef = useRef(false);
  quizFinishedRef.current = quizFinished;
  const questionStartTime = useRef<number>(Date.now());
  const writingDraftPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestPersistSnapshotRef = useRef<{
    sessionId: string;
    userAnswers: Array<{
      questionId: string;
      answer: string;
      isCorrect: boolean;
      timeSpent: number;
      sectionId?: string;
    }>;
    currentIndex: number;
    remainingTime: number;
  } | null>(null);

  // Load bank data and exam config
  useEffect(() => {
    const loadBank = async () => {
      setIsLoading(true);
      setError(null);

      if (launchGate.status === 'blocked') {
        setError(launchGate.message);
        setIsLoading(false);
        return;
      }

      let questionsForPaper: Question[] = [];
      let bankIdsForSession: string[] = [];

      try {
        if (mode === 'exam' || !bankId) {
          const examType = activeExamType;
          if (!examType) {
            setError('请先选择考试类型');
            setIsLoading(false);
            return;
          }

          let unlockedBankIds = new Set<string>();
          try {
            const res = await api.banks.myAccessible();
            const accessGate = buildLearnerContentAccessGate({
              accessibleBanks: res?.banks ?? [],
              examType,
              sectionId: effectiveTrainingSectionId,
            });

            if (accessGate.status !== 'ready') {
              setError(accessGate.message ?? '当前考试空间暂无可学习内容。');
              setIsLoading(false);
              return;
            }

            if (accessGate.candidates.length) {
              await Promise.all(
                accessGate.candidates.map((bank) =>
                  bankService.ensureBank(bank.id, bank.name, bank.version).catch(() => false)
                )
              );
              unlockedBankIds = buildLearnerAccessibleBankIdSet(accessGate.candidates);
            }
          } catch {
            const accessGate = buildLearnerContentAccessGate({
              accessibleBanks: [],
              examType,
              sectionId: effectiveTrainingSectionId,
              accessStatus: 'unavailable',
            });
            setError(accessGate.message ?? '暂时无法确认可学内容，请稍后重试。');
            setIsLoading(false);
            return;
          }
          const canUseBank = (id: string) => unlockedBankIds.has(id);

          const downloadedBanks = await bankService.getDownloadedBanks();
          const banksForExamType = downloadedBanks.filter(
            (bank) => bank.examType === examType && canUseBank(bank.id)
          );

          const targetSection = effectiveTrainingSectionId?.trim().toLowerCase() || null;
          const sectionScopedBanks = targetSection
            ? banksForExamType.filter(
                (bank) => String(bank.section || '').trim().toLowerCase() === targetSection
              )
            : [];
          // Prefer Skill-scoped inventories when present; whole-exam banks are fallback only.
          const banksToLoad =
            sectionScopedBanks.length > 0 ? sectionScopedBanks : banksForExamType;

          if (banksToLoad.length === 0) {
            setError('请先进入训练页准备内容，或通过兑换授权后继续练习');
            setIsLoading(false);
            return;
          }

          const allQuestions: Question[] = [];
          const sourceByQuestionId: Record<string, QuestionSourceTrace> = {};
          let mergedMeta: LocalBank['meta'] | null = null;

          for (const bank of banksToLoad) {
            const lb = await bankService.getLocalBank(bank.id);
            if (lb?.questions && Array.isArray(lb.questions)) {
              const bankSection = lb.meta.section || bank.section;
              for (const q of lb.questions) {
                const questionTrace = q as SectionedQuestion;
                sourceByQuestionId[q.id] = {
                  bankId: lb.meta.id,
                  bankName: lb.meta.name,
                  sourceType: questionTrace.sourceType ?? null,
                  sourceTaskId: questionTrace.sourceTaskId ?? null,
                  sourceMetadata: questionTrace.sourceMetadata ?? null,
                };
                const questionWithSection = questionTrace;
                if (!questionWithSection.section && bankSection) {
                  questionWithSection.section = bankSection;
                }
              }
              allQuestions.push(...lb.questions);
              if (!mergedMeta) {
                mergedMeta = {
                  ...lb.meta,
                  examType: lb.meta.examType || bank.examType,
                };
              }
            }
          }
          setQuestionSourceByQuestionId(sourceByQuestionId);

          if (allQuestions.length === 0) {
            setError('当前没有可用题目，请重新准备训练内容');
            setIsLoading(false);
            return;
          }

          const nowIso = new Date().toISOString();
          const mergedBank: LocalBank = {
            meta: mergedMeta || {
              id: `merged-${examType}`,
              name: '综合训练',
              examType,
              version: 'local',
              questionCount: allQuestions.length,
              downloadedAt: nowIso,
              updatedAt: nowIso,
            },
            questions: allQuestions,
          };

          setLocalBank(mergedBank);
          questionsForPaper = allQuestions;
          bankIdsForSession = banksToLoad.map((b) => b.id);
        } else {
          setQuestionSourceByQuestionId(null);
          bankIdsForSession = bankId ? [bankId] : [];
          let unlockedBankIds = new Set<string>();
          try {
            const res = await api.banks.myAccessible();
            const accessGate = buildLearnerContentAccessGate({
              accessibleBanks: res?.banks ?? [],
              examType: activeExamType || '',
              sectionId: effectiveTrainingSectionId,
            });

            if (accessGate.status !== 'ready') {
              setError(accessGate.message ?? ENTITLEMENT_EXPIRED_MESSAGE);
              setIsLoading(false);
              return;
            }

            unlockedBankIds = buildLearnerAccessibleBankIdSet(accessGate.candidates);
          } catch {
            const accessGate = buildLearnerContentAccessGate({
              accessibleBanks: [],
              examType: activeExamType || '',
              sectionId: effectiveTrainingSectionId,
              accessStatus: 'unavailable',
            });
            setError(accessGate.message ?? '暂时无法确认可学内容，请稍后重试。');
            setIsLoading(false);
            return;
          }
          const canUseBank = unlockedBankIds.has(bankId);
          if (!canUseBank) {
            setError(ENTITLEMENT_EXPIRED_MESSAGE);
            toast.error(ENTITLEMENT_EXPIRED_MESSAGE);
            setIsLoading(false);
            return;
          }

          const bank = await bankService.getLocalBank(bankId);
          if (!bank) {
            setError('训练内容尚未准备，请先进入训练页');
            setIsLoading(false);
            return;
          }

          if (!bank.questions || !Array.isArray(bank.questions) || bank.questions.length === 0) {
            setError('该训练内容暂无题目，请重新准备');
            setIsLoading(false);
            return;
          }

          const bankSection = bank.meta?.section;
          if (bankSection && bank.questions) {
            for (const q of bank.questions) {
              const questionWithSection = q as SectionedQuestion;
              if (!questionWithSection.section) {
                questionWithSection.section = bankSection;
              }
            }
          }

          setLocalBank(bank);
          questionsForPaper = bank.questions || [];
        }

        questionsForPaper = await applyCachedPlayableAudio(questionsForPaper);
        prefetchQuestionsAudio(questionsForPaper);

        if (effectiveTrainingSectionId) {
          try {
            const examType = activeExamType;
            if (examType) {
              const config = await loadExamConfig(examType);
              const sectionOption = config.sections.find(
                (section) => section.id === effectiveTrainingSectionId
              );
              setSectionName(sectionOption?.name ?? null);
            }
          } catch {
            // ignore
          }
        }

        if (mode === 'exam') {
          const examType = activeExamType;
          if (examType) {
            try {
              const config = await loadExamConfig(examType);
              setExamConfig(config);

              const launchLanguageId =
                launchGate.status === 'ready' ? launchGate.languageId : languageIdParam;

              const hydrateExamSession = async (session: ActiveSession) => {
                const questionMap = new Map(questionsForPaper.map((q) => [q.id, q]));
                const restored = session.questionIds
                  .map((id) => questionMap.get(id))
                  .filter((q): q is Question => q != null);
                if (restored.length === 0) return false;

                setPaper(null);
                setActiveSessionId(session.id);
                setRestoredSectionBoundaries(session.sectionBoundaries || null);
                setRestoredQuestions(restored);
                setCurrentIndex(session.currentIndex);
                setAnswers(
                  session.userAnswers.map((a) => ({
                    questionId: a.questionId,
                    answer: a.answer,
                    isCorrect: a.isCorrect,
                    timeSpent: a.timeSpent,
                    sectionId: a.sectionId,
                  }))
                );
                setUserSelections(
                  Object.fromEntries(session.userAnswers.map((a) => [a.questionId, a.answer]))
                );
                setRemainingTime(session.remainingTime);
                setExamDurationMs(session.remainingTime);

                const timerInstance = new TimerManager(session.remainingTime / 60000);
                timerInstance.setCallbacks(
                  (remaining) => {
                    setRemainingTime(remaining);
                  },
                  () => {
                    handleFinishExam();
                  }
                );
                setTimer(timerInstance);
                timerInstance.start();
                questionStartTime.current = Date.now();
                return true;
              };

              const urlSid = readQuizSessionId(
                new URLSearchParams(window.location.search)
              );
              if (urlSid && forceExamBuild === 0) {
                const byId = await hydrateQuizSession(urlSid);
                if (byId?.mode === 'exam' && byId.questionIds.length > 0) {
                  const ok = await hydrateExamSession(byId);
                  if (ok) {
                    setIsLoading(false);
                    return;
                  }
                }
                await abandonQuizSession(urlSid).catch(() => {});
                setSearchParams((prev) => withoutQuizSessionId(prev), { replace: true });
              }

              // No in-page resume prompt. Stale incomplete mock → abandon then create.
              if (forceExamBuild === 0 && !readQuizSessionId(new URLSearchParams(window.location.search))) {
                const existingSession = await findIncompleteExamSession({
                  examType,
                  languageId: launchLanguageId,
                });
                if (existingSession) {
                  await abandonQuizSession(existingSession.id).catch(() => {});
                }
              }

              setRestoredQuestions(null);
              setRestoredSectionBoundaries(null);
              const p = buildPaper(examType, config, questionsForPaper);
              setPaper(p);

              const { questions: flatQuestions, sectionBoundaries } = flattenPaper(p);
              const platformScope = buildPlatformScope({
                examType,
                languageId: launchLanguageId,
              });
              const totalDuration = config.sections.reduce(
                (sum: number, section: ExamSectionConfig) => sum + section.duration,
                0
              );
              const session = await createExamSession({
                examType,
                languageId: platformScope?.languageId ?? launchLanguageId,
                questionIds: flatQuestions.map((q) => q.id),
                remainingTimeMs: totalDuration * 60 * 1000,
                bankIds: bankIdsForSession.length > 0 ? bankIdsForSession : undefined,
                platformScope,
                sectionBoundaries,
              });
              setActiveSessionId(session.id);
              setSearchParams((prev) => withQuizSessionId(prev, session.id), { replace: true });

              const timerInstance = new TimerManager(totalDuration);
              timerInstance.setCallbacks(
                (remaining) => {
                  setRemainingTime(remaining);
                },
                () => {
                  handleFinishExam();
                }
              );
              setTimer(timerInstance);
              setRemainingTime(totalDuration * 60 * 1000);
              setExamDurationMs(totalDuration * 60 * 1000);
            } catch {
              // If config load fails, fall back to practice mode
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载训练内容失败');
      } finally {
        setIsLoading(false);
      }
    };

    loadBank();
  }, [bankLoadIdentity]);

  useEffect(() => {
    if (mode === 'practice') setPaper(null);
    if (mode === 'exam') {
      setPinnedPracticePaper(null);
      setForcePracticeCreate(0);
    }
  }, [mode]);

  useEffect(() => {
    if (effectiveTrainingSectionId) {
      const loadSectionName = async () => {
        try {
          const examType = activeExamType;
          if (examType) {
            const config = await loadExamConfig(examType);
            const sectionOption = config.sections.find(
              (section) => section.id === effectiveTrainingSectionId
            );
            setSectionName(sectionOption?.name ?? null);
          }
        } catch {
          setSectionName(null);
        }
      };
      loadSectionName();
    } else {
      setSectionName(null);
    }
  }, [activeExamType, effectiveTrainingSectionId]);

  useEffect(() => {
    if (mode === 'exam' && timer && !timer.getIsRunning() && localBank) {
      timer.start();
      questionStartTime.current = Date.now();
    }
    return () => {
      if (timer) timer.stop();
    };
  }, [timer, mode, localBank]);

  useEffect(() => {
    setUserSelections({});
    setShowAnalysisByQuestionId({});
  }, [bankId, effectiveTrainingSectionId, mode, questionCount, difficultyFilter]);

  const practiceFingerprintKey = useMemo(
    () => (practiceLaunchFingerprint ? JSON.stringify(practiceLaunchFingerprint) : null),
    [practiceLaunchFingerprint]
  );
  const prevPracticeFingerprintKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (mode !== 'practice') {
      prevPracticeFingerprintKeyRef.current = null;
      return;
    }
    if (!practiceFingerprintKey) return;
    if (prevPracticeFingerprintKeyRef.current === practiceFingerprintKey) return;
    const previous = prevPracticeFingerprintKeyRef.current;
    prevPracticeFingerprintKeyRef.current = practiceFingerprintKey;
    // First paint: let boot own create/hydrate.
    if (previous == null) return;
    // In-flight session (URL sessionId or memory id before replace) must not re-assemble.
    if (urlSessionId || activeSessionId) return;
    // Launch intent changed with no live session (e.g. level switch before start) → force new create.
    setPinnedPracticePaper(null);
    setActiveSessionId(null);
    setAnswers([]);
    setCurrentIndex(0);
    setUserSelections({});
  }, [mode, practiceFingerprintKey, urlSessionId, activeSessionId]);

  /**
   * Training Session Runtime boot: hydrate by sessionId, or create once.
   * Resume prompts belong to QuizRuntimeLaunchProvider — never here.
   * Assemble/shuffle only happens in the create branch.
   *
   * Do NOT depend on pinnedPracticePaper / activeSessionId — setting paper mid-boot
   * used to re-run this effect, cancel the in-flight create, and shuffle again ("题目乱跳").
   * Do NOT boot after quizFinished — clearing the running session on submit used to
   * re-create and setAnswers([]) before the result screen, showing「已作答 0」.
   */
  useEffect(() => {
    if (mode !== 'practice' || !localBank || isLoading || !practiceLaunchFingerprint) {
      return;
    }
    if (quizFinished) {
      return;
    }
    // Already bound to this URL session (or in-memory session before replace settles).
    if (pinnedPracticePaper && activeSessionId && (!urlSessionId || activeSessionId === urlSessionId)) {
      return;
    }

    let cancelled = false;

    const bootPracticeSession = async () => {
      setPracticeSessionBooting(true);
      try {
        if (urlSessionId && forcePracticeCreate === 0) {
          const session = await hydrateQuizSession(urlSessionId);
          if (cancelled) return;
          if (session?.mode === 'practice' && session.questionIds.length > 0) {
            const restored = resolveQuestionsByIds(localBank, session.questionIds);
            if (restored.length > 0) {
              setPinnedPracticePaper(
                practicePaperFromPinnedQuestions(restored, effectiveTrainingSectionId)
              );
              setActiveSessionId(session.id);
              setCurrentIndex(session.currentIndex);
              // Never clobber in-memory answers with a staler Dexie row (persist lag).
              setAnswers((prev) =>
                prev.length > 0
                  ? prev
                  : session.userAnswers.map((a) => ({
                      questionId: a.questionId,
                      answer: a.answer,
                      isCorrect: a.isCorrect,
                      timeSpent: a.timeSpent,
                      sectionId: a.sectionId,
                    }))
              );
              setUserSelections((prev) =>
                Object.keys(prev).length > 0
                  ? prev
                  : Object.fromEntries(session.userAnswers.map((a) => [a.questionId, a.answer]))
              );
              questionStartTime.current = Date.now();
              return;
            }
          }
          await abandonQuizSession(urlSessionId).catch(() => {});
          if (!cancelled) {
            setSearchParams((prev) => withoutQuizSessionId(prev), { replace: true });
          }
        }

        // Deep-link without sessionId: never prompt.
        // writing → silent hydrate; choice → abandon stale then create.
        if (forcePracticeCreate === 0 && !readQuizSessionId(new URLSearchParams(window.location.search))) {
          const existing = await findIncompletePracticeSession(practiceLaunchFingerprint);
          if (cancelled) return;
          if (existing) {
            const disposition = resolveIncompletePracticeDisposition({
              skillId: effectiveTrainingSectionId,
            });

            if (disposition === 'silent_continue') {
              const restored = resolveQuestionsByIds(localBank, existing.questionIds);
              if (restored.length === 0) {
                await abandonQuizSession(existing.id).catch(() => {});
              } else {
                setPinnedPracticePaper(
                  practicePaperFromPinnedQuestions(restored, effectiveTrainingSectionId)
                );
                setActiveSessionId(existing.id);
                setCurrentIndex(existing.currentIndex);
                setAnswers((prev) =>
                  prev.length > 0
                    ? prev
                    : existing.userAnswers.map((a) => ({
                        questionId: a.questionId,
                        answer: a.answer,
                        isCorrect: a.isCorrect,
                        timeSpent: a.timeSpent,
                        sectionId: a.sectionId,
                      }))
                );
                setUserSelections((prev) =>
                  Object.keys(prev).length > 0
                    ? prev
                    : Object.fromEntries(existing.userAnswers.map((a) => [a.questionId, a.answer]))
                );
                setSearchParams((prev) => withQuizSessionId(prev, existing.id), { replace: true });
                questionStartTime.current = Date.now();
                return;
              }
            } else {
              await abandonQuizSession(existing.id).catch(() => {});
            }
          }
        }

        const assembled = assemblePracticeQuestionList({
          questions: localBank.questions || [],
          examType: activeExamType || localBank.meta?.examType || null,
          sectionId: effectiveTrainingSectionId,
          difficultyFilter,
          taskNumberFilter: writingTaskNumberFilter,
          topicIdFilter: writingTopicIdFilter,
          preferredQuestionId: resumeQuestionId,
          questionCount,
          isDiagnosisPack: trainingAttemptContext?.source === 'diagnosis',
        });
        if (cancelled || quizFinishedRef.current) return;
        if (assembled.questions.length === 0) {
          setPinnedPracticePaper(assembled);
          setActiveSessionId(null);
          return;
        }

        const platformScope = buildPlatformScope({
          examType: practiceLaunchFingerprint.examType,
          languageId: practiceLaunchFingerprint.languageId,
        });
        const session = await createPracticeSession({
          fingerprint: practiceLaunchFingerprint,
          questionIds: assembled.questions.map((q) => q.id),
          bankIds: bankId ? [bankId] : undefined,
          platformScope,
        });
        if (cancelled || quizFinishedRef.current) {
          await abandonQuizSession(session.id).catch(() => {});
          return;
        }
        // Pin paper only after session exists — never mid-boot (avoids re-shuffle races).
        setPinnedPracticePaper(assembled);
        setActiveSessionId(session.id);
        setCurrentIndex(0);
        // Only reset answers when starting a true new pack (not a finish-path re-entry).
        setAnswers((prev) => (prev.length > 0 ? prev : []));
        setUserSelections((prev) => (Object.keys(prev).length > 0 ? prev : {}));
        setSearchParams((prev) => withQuizSessionId(prev, session.id), { replace: true });
        questionStartTime.current = Date.now();
      } finally {
        if (!cancelled) setPracticeSessionBooting(false);
      }
    };

    void bootPracticeSession();
    return () => {
      cancelled = true;
    };
    // pinnedPracticePaper / activeSessionId intentionally omitted from deps (read for guard only).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [
    mode,
    localBank,
    isLoading,
    practiceLaunchFingerprint,
    urlSessionId,
    forcePracticeCreate,
    quizFinished,
    effectiveTrainingSectionId,
    activeExamType,
    difficultyFilter,
    writingTaskNumberFilter,
    writingTopicIdFilter,
    resumeQuestionId,
    questionCount,
    trainingAttemptContext?.source,
    bankId,
    setSearchParams,
  ]);

  const practicePaper = useMemo(() => {
    if (mode === 'exam') {
      if (restoredQuestions && restoredQuestions.length > 0) {
        return {
          questions: restoredQuestions,
          openResponseOnly: false,
          sessionResponseMode: 'closed' as const,
          rawCount: restoredQuestions.length,
          usableChoiceCount: restoredQuestions.length,
          usableOpenEndedCount: 0,
        };
      }
      if (paper) {
        const { questions: flatQuestions } = flattenPaper(paper);
        return {
          questions: flatQuestions,
          openResponseOnly: false,
          sessionResponseMode: 'closed' as const,
          rawCount: flatQuestions.length,
          usableChoiceCount: flatQuestions.length,
          usableOpenEndedCount: 0,
        };
      }
      return {
        questions: [] as Question[],
        openResponseOnly: false,
        sessionResponseMode: 'closed' as const,
        rawCount: 0,
        usableChoiceCount: 0,
        usableOpenEndedCount: 0,
      };
    }

    if (quizFinished && finishedPracticePaper) {
      return finishedPracticePaper;
    }

    if (pinnedPracticePaper) {
      return pinnedPracticePaper;
    }

    return {
      questions: [] as Question[],
      openResponseOnly: false,
      sessionResponseMode: 'closed' as const,
      rawCount: 0,
      usableChoiceCount: 0,
      usableOpenEndedCount: 0,
    };
  }, [mode, paper, restoredQuestions, quizFinished, finishedPracticePaper, pinnedPracticePaper]);

  const questions = practicePaper.questions;
  const isOpenEndedWritingSession =
    mode === 'practice' && practicePaper.sessionResponseMode === 'open_ended';

  useEffect(() => {
    if (!isOpenEndedWritingSession) {
      setWritingElapsedSeconds(0);
      setWritingClockPaused(false);
      return;
    }
    setWritingElapsedSeconds(0);
    setWritingClockPaused(false);
  }, [isOpenEndedWritingSession, currentIndex]);

  useEffect(() => {
    if (!isOpenEndedWritingSession || writingClockPaused) return;
    const timerId = window.setInterval(() => {
      setWritingElapsedSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [isOpenEndedWritingSession, currentIndex, writingClockPaused]);

  useEffect(() => {
    if (mode !== 'practice') {
      practiceStartedAtRef.current = null;
      practiceElapsedMsRef.current = 0;
      setPracticeElapsedMs(0);
      setPracticeClockPaused(false);
      return;
    }
    if (quizFinished || isLoading || questions.length === 0) {
      return;
    }
    practiceElapsedMsRef.current = 0;
    practiceStartedAtRef.current = Date.now();
    setPracticeElapsedMs(0);
    setPracticeClockPaused(false);
  }, [mode, quizFinished, isLoading, questions.length, questions[0]?.id]);

  useEffect(() => {
    if (mode !== 'practice' || quizFinished || isLoading || questions.length === 0) {
      return;
    }
    if (practiceClockPaused) return;
    practiceStartedAtRef.current = Date.now() - practiceElapsedMsRef.current;
    const timerId = window.setInterval(() => {
      const startedAt = practiceStartedAtRef.current ?? Date.now();
      const next = Math.max(0, Date.now() - startedAt);
      practiceElapsedMsRef.current = next;
      setPracticeElapsedMs(next);
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [mode, quizFinished, isLoading, questions.length, questions[0]?.id, practiceClockPaused]);

  const handleTogglePracticeClockPause = useCallback(() => {
    setPracticeClockPaused((paused) => !paused);
  }, []);

  const handleResetPracticeClock = useCallback(() => {
    practiceElapsedMsRef.current = 0;
    practiceStartedAtRef.current = Date.now();
    setPracticeElapsedMs(0);
  }, []);

  const handleToggleWritingClockPause = useCallback(() => {
    setWritingClockPaused((paused) => !paused);
  }, []);

  const handleResetWritingClock = useCallback(() => {
    setWritingElapsedSeconds(0);
  }, []);

  // Keep snapshot in sync during render so pagehide / effect cleanup never flush a stale empty row.
  if (activeSessionId && questions.length > 0 && !quizFinished) {
    latestPersistSnapshotRef.current = {
      sessionId: activeSessionId,
      userAnswers: answers.map((a) => ({
        questionId: a.questionId,
        answer: a.answer,
        isCorrect: a.isCorrect,
        timeSpent: a.timeSpent,
        sectionId: a.sectionId,
      })),
      currentIndex,
      remainingTime: mode === 'exam' ? remainingTime : 0,
    };
  } else if (!activeSessionId || quizFinished) {
    latestPersistSnapshotRef.current = null;
  }

  // Persist in-progress Session to IndexedDB. Resume dialog reads this row —
  // dropping a pending debounce without flush used to show「已答 0 题」after real answers.
  const clearPendingPersist = useCallback(() => {
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
      persistDebounceRef.current = null;
    }
  }, []);

  const persistActiveExamSession = useCallback(async () => {
    const snapshot = latestPersistSnapshotRef.current;
    if (!snapshot) return;

    await updateQuizSessionProgress(snapshot.sessionId, {
      userAnswers: snapshot.userAnswers,
      currentIndex: snapshot.currentIndex,
      remainingTime: snapshot.remainingTime,
    });
  }, []);

  const flushActiveSessionPersist = useCallback(() => {
    clearPendingPersist();
    void persistActiveExamSession().catch((err) =>
      console.error('[QuizPage] Persist active session flush failed:', err)
    );
  }, [clearPendingPersist, persistActiveExamSession]);

  useEffect(() => {
    if (!activeSessionId || !questions.length || quizFinished) {
      clearPendingPersist();
      return;
    }

    clearPendingPersist();
    const delayMs = answers.length > 0 ? 50 : 300;
    persistDebounceRef.current = setTimeout(() => {
      persistDebounceRef.current = null;
      persistActiveExamSession().catch((err) =>
        console.error('[QuizPage] Persist active session failed:', err)
      );
    }, delayMs);

    return () => {
      flushActiveSessionPersist();
    };
  }, [
    mode,
    activeSessionId,
    answers,
    currentIndex,
    remainingTime,
    questions.length,
    quizFinished,
    clearPendingPersist,
    persistActiveExamSession,
    flushActiveSessionPersist,
  ]);

  useEffect(() => {
    const onPageHide = () => {
      flushActiveSessionPersist();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onPageHide);
      flushActiveSessionPersist();
    };
  }, [flushActiveSessionPersist]);

  const currentQuestion = questions[currentIndex];

  const writingDraftScope = useMemo(() => {
    if (!isOpenEndedWritingSession) return null;
    const examType =
      activeExamType || examTypeParam || trainingPackParam || localBank?.meta?.examType || null;
    if (!examType) return null;
    const languageId =
      languageIdParam ||
      buildPlatformScope({ examType })?.languageId ||
      null;
    if (!languageId) return null;
    return {
      languageId,
      examType,
      skillId: effectiveTrainingSectionId || 'writing',
    };
  }, [
    isOpenEndedWritingSession,
    activeExamType,
    examTypeParam,
    trainingPackParam,
    localBank?.meta?.examType,
    languageIdParam,
    effectiveTrainingSectionId,
  ]);

  // Orphan resume pin (e.g. deep link) to a rejected cloze must clear the draft so it cannot trap retries.
  useEffect(() => {
    if (mode !== 'practice' || !resumeQuestionId || !writingDraftScope) return;
    if (isLoading) return;
    const resumed = questions.some((question) => question.id === resumeQuestionId);
    if (resumed) return;
    void writingDraftStore.delete(writingDraftScope, resumeQuestionId);
  }, [mode, resumeQuestionId, writingDraftScope, isLoading, questions]);

  // Hydrate writing draft from Dexie when the open-response question is ready.
  useEffect(() => {
    if (!isOpenEndedWritingSession || !writingDraftScope || !currentQuestion) return;
    let cancelled = false;
    void writingDraftStore.get(writingDraftScope, currentQuestion.id).then((record) => {
      if (cancelled) return;
      if (!record) return;
      setWritingDrafts((prev) => {
        if (prev[currentQuestion.id] != null && prev[currentQuestion.id] !== '') return prev;
        return { ...prev, [currentQuestion.id]: record.essay };
      });
      setWritingDraftSavedAt((prev) => ({
        ...prev,
        [currentQuestion.id]: record.updatedAt,
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [isOpenEndedWritingSession, writingDraftScope, currentQuestion?.id]);

  useEffect(() => {
    return () => {
      if (writingDraftPersistTimerRef.current) {
        clearTimeout(writingDraftPersistTimerRef.current);
        writingDraftPersistTimerRef.current = null;
      }
    };
  }, []);

  const handleWritingDraftChange = useCallback(
    (text: string) => {
      if (!currentQuestion) return;
      const questionId = currentQuestion.id;
      setWritingDrafts((prev) => ({ ...prev, [questionId]: text }));
      setWritingDraftSavedAt((prev) => ({ ...prev, [questionId]: null }));

      if (writingDraftPersistTimerRef.current) {
        clearTimeout(writingDraftPersistTimerRef.current);
      }
      if (!writingDraftScope) return;

      setWritingDraftSavePending(true);
      writingDraftPersistTimerRef.current = setTimeout(() => {
        void writingDraftStore
          .upsert(writingDraftScope, questionId, text)
          .then((savedAt) => {
            setWritingDraftSavedAt((prev) => ({ ...prev, [questionId]: savedAt }));
          })
          .catch(() => {
            setWritingDraftSavedAt((prev) => ({ ...prev, [questionId]: null }));
          })
          .finally(() => {
            setWritingDraftSavePending(false);
          });
      }, 800);
    },
    [currentQuestion, writingDraftScope]
  );

  // Load note when question changes
  useEffect(() => {
    if (currentQuestion) {
      const savedNote = getNote(currentQuestion.id);
      setNoteText(savedNote || '');
    }
  }, [currentQuestion?.id]);

  const answeredSet = useMemo(() => {
    const fromAnswers = new Set(
      answers.map((a) => questions.findIndex((q) => q.id === a.questionId)).filter((i) => i >= 0)
    );
    const fromSelections = new Set(
      questions.map((q, idx) => (userSelections[q.id] ? idx : -1)).filter((i) => i >= 0)
    );
    return new Set([...fromAnswers, ...fromSelections]);
  }, [answers, questions, userSelections]);

  const answerSheetSections = useMemo(() => {
    if (mode !== 'exam') return undefined;
    if (paper) {
      return buildAnswerSheetBySection(paper, answeredSet, markedForReview);
    }
    if (restoredSectionBoundaries && restoredSectionBoundaries.length > 0) {
      return {
        sections: restoredSectionBoundaries.map((b) => {
          const indices = Array.from(
            { length: b.endIndex - b.startIndex },
            (_, i) => b.startIndex + i
          );
          return {
            sectionId: b.sectionId,
            sectionName: b.sectionName,
            questionTypes: [],
            indices,
            items: indices.map((idx) => ({
              index: idx,
              answered: answeredSet.has(idx),
              marked: markedForReview.has(idx),
            })),
          };
        }),
      };
    }
    return undefined;
  }, [mode, paper, restoredSectionBoundaries, answeredSet, markedForReview]);

  const selectedAnswer = currentQuestion ? (userSelections[currentQuestion.id] ?? null) : null;
  const showResult = currentQuestion
    ? answers.some((a) => a.questionId === currentQuestion.id)
    : false;
  const showExplanation = currentQuestion
    ? (showAnalysisByQuestionId[currentQuestion.id] ?? false)
    : false;

  const handleSelectAnswer = (optionId: string) => {
    if (!currentQuestion) return;
    if (showResult) return;
    setUserSelections((prev) => ({ ...prev, [currentQuestion.id]: optionId }));
    if (mode === 'practice') {
      submitWithAnswer(optionId);
      setShowAnalysisByQuestionId((prev) => ({ ...prev, [currentQuestion.id]: true }));
    }
  };

  const submitWithAnswer = (optionId: string) => {
    if (!currentQuestion) return null;
    const timeSpent = Date.now() - questionStartTime.current;
    const isCorrect = isAnswerCorrect(currentQuestion, optionId);
    const questionSectionId =
      effectiveTrainingSectionId || (currentQuestion as SectionedQuestion).section;
    const answer = {
      questionId: currentQuestion.id,
      answer: optionId,
      isCorrect,
      timeSpent,
      sectionId: questionSectionId,
    };
    if (!isCorrect) {
      void api.mistakes.saveMistake(currentQuestion.id, optionId).catch(() => {
        // Mistake index is best-effort; OSS-only questions may not be in DB yet.
      });
    }
    setAnswers((prev) => {
      if (prev.some((item) => item.questionId === currentQuestion.id)) {
        return prev;
      }
      return [...prev, answer];
    });
    return answer;
  };

  const handleSubmit = () => {
    if (!selectedAnswer || !currentQuestion) return;
    if (mode === 'practice') {
      submitWithAnswer(selectedAnswer);
    } else {
      const submittedAnswer = submitWithAnswer(selectedAnswer);
      void handleNext(submittedAnswer ?? undefined);
    }
  };

  const handleSkip = () => {
    if (mode === 'exam') return;
    void handleNext();
  };

  const handleFeedbackSubmit = async (data: QuestionFeedbackData) => {
    await api.questions.submitFeedback(data.questionId, {
      type: data.type,
      description: data.description,
    });
    toast.success('感谢您的反馈，我们会尽快处理');
  };

  const clearRunningQuizSession = useCallback(
    async (options?: { keepPinnedPaper?: boolean }) => {
      if (!activeSessionId) return;
      await completeQuizSession(activeSessionId).catch(() => {});
      latestPersistSnapshotRef.current = null;
      setActiveSessionId(null);
      // Keep pinned paper for QuizResult review rows when ending a answered round.
      if (!options?.keepPinnedPaper) {
        setPinnedPracticePaper(null);
      }
      setSearchParams((prev) => withoutQuizSessionId(prev), { replace: true });
    },
    [activeSessionId, setSearchParams]
  );

  const markPracticeRoundFinished = useCallback(
    (finalAnswers: SubmittedQuizAnswer[]) => {
      const paperSnapshot =
        pinnedPracticePaper ??
        practicePaperFromPinnedQuestions(questions, effectiveTrainingSectionId);
      setFinishedPracticePaper(paperSnapshot);
      setAnswers(finalAnswers);
      setQuizFinished(true);
    },
    [pinnedPracticePaper, questions, effectiveTrainingSectionId]
  );

  const handleFinishExam = async () => {
    clearPendingPersist();
    if (timer) timer.stop();
    if (mode === 'practice') {
      if (answers.length === 0) {
        await clearRunningQuizSession();
        navigate(exitHref);
        return;
      }
      // Snapshot + finish flag before clearing session so boot/empty-exit cannot wipe result.
      markPracticeRoundFinished(answers);
      await persistTrainingAttempt(answers);
      await clearRunningQuizSession({ keepPinnedPaper: true });
      return;
    }
    if (mode === 'exam' && activeSessionId && examConfig && questions.length > 0) {
      const session = await hydrateQuizSession(activeSessionId);
      const correctCount = answers.filter((a) => a.isCorrect).length;
      const userAnswersForCalc = answers.map((a) => ({
        questionId: a.questionId,
        answer: a.answer,
        isCorrect: a.isCorrect,
        timeSpent: a.timeSpent,
        timestamp: Date.now(),
      }));
      const scoreResult = ScoreCalculator.calculate(
        userAnswersForCalc,
        questions,
        examConfig.scoringRules
      );
      const startedAt =
        answers.length > 0
          ? Date.now() - answers.reduce((sum, a) => sum + a.timeSpent, 0)
          : Date.now();
      const recordExamType = examConfig.id || activeExamType;
      if (!recordExamType) {
        await completeQuizSession(activeSessionId).catch(() => {});
        latestPersistSnapshotRef.current = null;
        setActiveSessionId(null);
        setQuizFinished(true);
        return;
      }
      const platformScope = buildPlatformScope({
        examType: recordExamType,
        languageId: launchGate.status === 'ready' ? launchGate.languageId : languageIdParam,
      });
      const recordId = `record-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      await examSessionRepository
        .saveExamRecord({
          id: recordId,
          examType: recordExamType,
          languageId: platformScope?.languageId ?? null,
          locale: platformScope?.locale ?? null,
          ttsLocale: platformScope?.ttsLocale ?? null,
          platformScope,
          bankIds: session?.bankIds ?? [],
          totalQuestions: questions.length,
          correctCount,
          score: scoreResult?.score ?? 0,
          totalScore: scoreResult?.totalScore ?? 0,
          passed: scoreResult?.passed ?? false,
          duration: Date.now() - startedAt,
          completedAt: Date.now(),
          questionIds: questions.map((q) => q.id),
          userAnswers: answers.map((a) => ({
            questionId: a.questionId,
            answer: a.answer,
            isCorrect: a.isCorrect,
            timeSpent: a.timeSpent,
            sectionId: a.sectionId,
          })),
        })
        .catch(() => {});
      await completeQuizSession(activeSessionId).catch(() => {});
      setSearchParams((prev) => withoutQuizSessionId(prev), { replace: true });
      // 能力快照是模考权威证据：失败要提示，不能只写本机记录假装已进诊断。
      try {
        if (!platformScope || answers.length === 0) {
          toast.error('模考未形成可上报的能力证据，诊断页不会看到这次模考。');
        } else {
          const questionById = new Map(questions.map((question) => [question.id, question]));
          const dimensionMap = new Map<
            string,
            { sectionId: string; label: string; answeredCount: number; correctCount: number }
          >();
          answers.forEach((answer) => {
            const sectionId =
              answer.sectionId ||
              (questionById.get(answer.questionId) as SectionedQuestion | undefined)?.section;
            if (!sectionId) return;
            const previous = dimensionMap.get(sectionId) ?? {
              sectionId,
              label:
                examConfig.sections?.find((section) => section.id === sectionId)?.name ?? sectionId,
              answeredCount: 0,
              correctCount: 0,
            };
            dimensionMap.set(sectionId, {
              ...previous,
              answeredCount: previous.answeredCount + 1,
              correctCount: previous.correctCount + (answer.isCorrect ? 1 : 0),
            });
          });
          const dimensions = Array.from(dimensionMap.values()).map((dimension) => ({
            ...dimension,
            accuracy:
              dimension.answeredCount > 0
                ? Math.round((dimension.correctCount / dimension.answeredCount) * 100)
                : 0,
          }));
          if (dimensions.length === 0) {
            toast.error('模考未形成可上报的能力维度，诊断页不会看到这次模考。');
          } else {
            await attemptEvidenceClient.saveMockAbilityEvidence({
              platformScope,
              dimensions,
              sourceRefId: recordId,
            });
          }
        }
      } catch {
        toast.error('模考成绩已出，但能力证据同步失败。请稍后打开能力诊断确认。');
      }
      latestPersistSnapshotRef.current = null;
      setActiveSessionId(null);
    }
    setQuizFinished(true);
  };

  const persistTrainingAttempt = useCallback(
    async (finalAnswers: SubmittedQuizAnswer[]) => {
      if (mode !== 'practice' || !trainingAttemptContext || questions.length === 0) {
        return;
      }

      const payload = buildTrainingAttemptPersistPayload({
        finalAnswers,
        questions: questions as Array<{
          id: string;
          section?: string;
          level?: string | null;
          type?: string;
          sourceType?: string | null;
          sourceTaskId?: string | null;
          sourceMetadata?: unknown;
        }>,
        trainingAttemptContext,
        questionSourceByQuestionId,
      });
      if (!payload) return;

      await attemptEvidenceClient
        .saveTrainingEvidence(payload)
        .then(async ({ record }) => {
          if (
            trainingAttemptContext.source === 'diagnosis' &&
            payload.platformScope &&
            payload.sectionSummaries.length
          ) {
            await attemptEvidenceClient
              .saveDiagnosisAbilityEvidence({
                platformScope: payload.platformScope,
                dimensions: payload.sectionSummaries.map((summary) => ({
                  sectionId: summary.sectionId,
                  label: summary.sectionLabel,
                  accuracy: summary.accuracy,
                  answeredCount: summary.answeredCount,
                  correctCount: summary.correctCount,
                })),
                overallScore: payload.accuracy,
                sourceRefId: record?.id ?? null,
              })
              .catch(() => {});
          }
          checkAndUnlockAchievements({
            correctCount: payload.correctCount,
            answeredCount: payload.answeredCount,
            accuracy: payload.accuracy,
            durationSeconds: payload.durationSeconds,
            questionCount: questions.length,
          });
        })
        .catch((error) => {
          console.error('[QuizPage] 保存训练记录失败:', error);
          toast.error('训练记录保存失败，请稍后重新进入训练页确认。');
        });
    },
    [
      mode,
      questionSourceByQuestionId,
      questions,
      toast,
      trainingAttemptContext,
      checkAndUnlockAchievements,
    ]
  );

  const exitHref = resolveAttemptExitHref({
    languageId:
      launchGate.status === 'ready'
        ? launchGate.languageId
        : (trainingAttemptContext?.languageId ?? languageIdParam),
    examType:
      launchGate.status === 'ready'
        ? launchGate.examType
        : (activeExamType ?? examTypeParam ?? trainingPackParam),
  });

  // Empty Quiz Runtime is not a destination — leave for Exam Space.
  // Never steal the result screen (quizFinishedRef flips synchronously with setState).
  const emptyPracticeExitRef = useRef(false);
  useEffect(() => {
    if (questions.length > 0 || quizFinishedRef.current) {
      emptyPracticeExitRef.current = false;
      return;
    }
    if (mode !== 'practice' || quizFinished || isLoading || practiceSessionBooting || error) {
      return;
    }
    if (!localBank || !pinnedPracticePaper) return;
    if (emptyPracticeExitRef.current) return;
    emptyPracticeExitRef.current = true;
    toast.error('当前训练暂无可练题目，已返回考试空间');
    navigate(exitHref, { replace: true });
  }, [
    mode,
    quizFinished,
    isLoading,
    practiceSessionBooting,
    error,
    localBank,
    pinnedPracticePaper,
    questions.length,
    toast,
    navigate,
    exitHref,
  ]);

  const handleExit = () => {
    setShowExitConfirm(true);
  };

  const confirmExit = async () => {
    setShowExitConfirm(false);
    if (timer) timer.stop();
    clearPendingPersist();
    if (mode === 'practice') {
      if (answers.length > 0) {
        markPracticeRoundFinished(answers);
        if (trainingAttemptContext) {
          await persistTrainingAttempt(answers);
        }
        await clearRunningQuizSession({ keepPinnedPaper: true });
        return;
      }
      navigate(exitHref);
      return;
    }
    await persistActiveExamSession().catch((err) =>
      console.error('[QuizPage] Persist active session before exit failed:', err)
    );
    navigate(exitHref);
  };

  const handleRestartExam = async () => {
    setShowRestartConfirm(false);
    clearPendingPersist();
    latestPersistSnapshotRef.current = null;
    if (timer) timer.stop();
    if (activeSessionId) {
      await abandonQuizSession(activeSessionId).catch(() => {});
    }
    setActiveSessionId(null);
    setAnswers([]);
    setCurrentIndex(0);
    setUserSelections({});
    setShowAnalysisByQuestionId({});
    setQuizFinished(false);
    setFinishedPracticePaper(null);
    setMarkedForReview(new Set());
    setRestoredQuestions(null);
    setRestoredSectionBoundaries(null);
    setPinnedPracticePaper(null);
    setPaper(null);
    setTimer(null);
    setRemainingTime(0);
    setExamDurationMs(0);
    setSearchParams((prev) => withoutQuizSessionId(prev), { replace: true });
    if (mode === 'practice') {
      setForcePracticeCreate((f) => f + 1);
    } else {
      setForceExamBuild((f) => f + 1);
    }
    toast.success('已重新开始训练');
  };

  const handleNext = async (submittedAnswer?: {
    questionId: string;
    answer: string;
    isCorrect: boolean;
    timeSpent: number;
    sectionId?: string;
  }) => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      questionStartTime.current = Date.now();
    } else {
      if (mode === 'exam') {
        setShowSubmitConfirm(true);
      } else {
        let finalAnswers =
          submittedAnswer &&
          !answers.some((answer) => answer.questionId === submittedAnswer.questionId)
            ? [...answers, submittedAnswer]
            : answers;
        if (
          mode === 'practice' &&
          currentQuestion &&
          selectedAnswer &&
          !finalAnswers.some((answer) => answer.questionId === currentQuestion.id)
        ) {
          const fallbackAnswer = submitWithAnswer(selectedAnswer);
          if (fallbackAnswer) {
            finalAnswers = [...finalAnswers, fallbackAnswer];
          }
        }
        if (finalAnswers.length === 0) {
          toast.error('还没有作答，可直接离开或继续答题');
          return;
        }
        // Snapshot + finish flag before clearing session — otherwise boot/empty-exit wipe result.
        markPracticeRoundFinished(finalAnswers);
        await persistTrainingAttempt(finalAnswers);
        await clearRunningQuizSession({ keepPinnedPaper: true });
      }
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      questionStartTime.current = Date.now();
    }
  };

  const toggleMarkForReview = () => {
    setMarkedForReview((prev) => {
      const next = new Set(prev);
      if (next.has(currentIndex)) next.delete(currentIndex);
      else next.add(currentIndex);
      return next;
    });
  };

  const goToQuestion = (index: number) => {
    setCurrentIndex(index);
    questionStartTime.current = Date.now();
  };

  // --- Render ---

  // Finished round wins over loading/empty — session clear must not hide QuizResult.
  if (quizFinished) {
    return (
      <QuizResult
        mode={mode}
        answers={answers}
        questions={questions}
        examConfig={examConfig}
        timer={timer}
        elapsedMs={
          mode === 'exam'
            ? (timer?.getElapsed() ?? Math.max(0, examDurationMs - remainingTime))
            : practiceElapsedMs
        }
        trainingContext={trainingAttemptContext}
        examContext={
          mode === 'exam'
            ? {
                examType: examConfig?.id || activeExamType || 'unknown',
                examLabel:
                  examConfig?.name ||
                  (activeExamType ? activeExamType.toUpperCase() : '当前考试'),
              }
            : null
        }
        onRestart={() => {
          setCurrentIndex(0);
          setUserSelections({});
          setShowAnalysisByQuestionId({});
          setAnswers([]);
          setQuizFinished(false);
          setFinishedPracticePaper(null);
          if (mode === 'practice') {
            setPinnedPracticePaper(null);
            setForcePracticeCreate((f) => f + 1);
          } else if (timer) {
            timer.reset();
            timer.start();
          }
          questionStartTime.current = Date.now();
        }}
      />
    );
  }

  if (isLoading || (mode === 'practice' && practiceSessionBooting && !pinnedPracticePaper)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !localBank) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="card text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h2 className="text-2xl font-bold text-slate-900 mb-4">{error || '加载训练内容失败'}</h2>
          <p className="text-slate-500 mb-6">{error || '无法加载训练内容，请检查是否已完成准备'}</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() =>
                navigate(
                  launchGate.status === 'blocked' ? launchGate.workspaceHref : '/workspace'
                )
              }
              className="btn-primary"
            >
              返回考试空间
            </button>
            <button onClick={() => navigate('/redeem')} className="btn-outline">
              输入兑换码
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="card text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-warning-400" />
          <h2 className="text-2xl font-bold text-slate-900 mb-4">题目加载错误</h2>
          <p className="text-slate-500 mb-6">无法加载当前题目，请刷新页面重试。</p>
          <button onClick={() => window.location.reload()} className="btn-primary">
            刷新页面
          </button>
        </div>
      </div>
    );
  }

  const handleWritingComplete = async (payload: {
    essay: string;
    wordCount: number;
    grade: {
      score: number;
      scoreMax: number;
      levelHint: string;
      feedback: string;
      strengths: string[];
      improvements: string[];
      dimensions: Array<{ id: string; label: string; score: number; action: string }>;
      isHeuristic?: boolean;
    };
    timeSpentMs: number;
    revisionCount: number;
    previousEssay?: string;
    previousScore?: number;
  }) => {
    if (!currentQuestion) return;
    const questionSectionId =
      effectiveTrainingSectionId || (currentQuestion as SectionedQuestion).section;
    const feedbackParts = [
      payload.grade.feedback,
      payload.grade.improvements.length
        ? `优先改：${payload.grade.improvements.join('；')}`
        : '',
      payload.revisionCount > 0 && payload.previousScore != null
        ? `修订：${payload.previousScore} → ${payload.grade.score}`
        : '',
    ].filter(Boolean);
    const answer: SubmittedQuizAnswer = {
      questionId: currentQuestion.id,
      answer: payload.essay,
      isCorrect: false,
      timeSpent: payload.timeSpentMs,
      sectionId: questionSectionId,
      responseMode: 'open_ended',
      wordCount: payload.wordCount,
      aiScore: payload.grade.score,
      aiScoreMax: payload.grade.scoreMax,
      aiGradeSource: payload.grade.isHeuristic ? 'heuristic' : 'llm',
      aiLevelHint: payload.grade.levelHint,
      aiFeedback: feedbackParts.join('\n'),
      aiDimensions: payload.grade.dimensions,
    };
    const finalAnswers = answers.some((item) => item.questionId === currentQuestion.id)
      ? answers.map((item) => (item.questionId === currentQuestion.id ? answer : item))
      : [...answers, answer];
    markPracticeRoundFinished(finalAnswers);
    await persistTrainingAttempt(finalAnswers);
    if (writingDraftScope) {
      await writingDraftStore.delete(writingDraftScope, currentQuestion.id);
      setWritingDrafts((prev) => {
        const next = { ...prev };
        delete next[currentQuestion.id];
        return next;
      });
      setWritingDraftSavedAt((prev) => {
        const next = { ...prev };
        delete next[currentQuestion.id];
        return next;
      });
    }
    await clearRunningQuizSession({ keepPinnedPaper: true });
  };

  const isLastQuestion = currentIndex === questions.length - 1;
  const examType = activeExamType || localBank?.meta?.examType;
  const sectionDisplayName =
    sectionName || (mode === 'practice' ? '练习' : examType?.toUpperCase() || '');
  const examLabel = examType?.toUpperCase() || '练习';

  if (isOpenEndedWritingSession) {
    const writingDisplay = resolveWritingDisplayLabels(
      currentQuestion,
      activeExamType || examTypeParam || trainingPackParam || 'tcf'
    );
    return (
      <>
        <WritingSessionChrome
          examLabel={examLabel}
          sectionName={sectionDisplayName || examLabel}
          summaryLine={writingDisplay.chromeSummaryLine}
          onExit={handleExit}
        >
          <WritingSessionPanel
            question={currentQuestion}
            questionNumber={currentIndex + 1}
            languageId={
              languageIdParam ||
              buildPlatformScope({ examType: activeExamType || examTypeParam })?.languageId ||
              null
            }
            examId={activeExamType || examTypeParam || trainingPackParam || 'tcf'}
            skillId={effectiveTrainingSectionId || 'writing'}
            draftScope={writingDraftScope}
            draftText={writingDrafts[currentQuestion.id] ?? ''}
            draftSavedAt={writingDraftSavedAt[currentQuestion.id] ?? null}
            draftSavePending={writingDraftSavePending}
            elapsedSeconds={writingElapsedSeconds}
            clockPaused={writingClockPaused}
            onToggleClockPause={handleToggleWritingClockPause}
            onResetClock={handleResetWritingClock}
            onDraftChange={handleWritingDraftChange}
            onComplete={(payload) => void handleWritingComplete(payload)}
            onViewHistory={() => setShowWritingHistory(true)}
          />
        </WritingSessionChrome>
        {showWritingHistory && (
          <WritingHistoryPanel
            examType={activeExamType || examTypeParam || trainingPackParam || 'tcf'}
            skillId={effectiveTrainingSectionId || 'writing'}
            onClose={() => setShowWritingHistory(false)}
          />
        )}
        <QuizModals
          mode={mode === 'exam' ? 'exam' : 'practice'}
          showExitConfirm={showExitConfirm}
          onCancelExit={() => setShowExitConfirm(false)}
          onConfirmExit={confirmExit}
          showSubmitConfirm={showSubmitConfirm}
          onCancelSubmit={() => setShowSubmitConfirm(false)}
          onConfirmSubmit={() => {
            setShowSubmitConfirm(false);
            void handleFinishExam();
          }}
          totalQuestions={questions.length}
          answeredCount={answers.length}
          showRestartConfirm={showRestartConfirm}
          onCancelRestart={() => setShowRestartConfirm(false)}
          onConfirmRestart={handleRestartExam}
        />
      </>
    );
  }

  const handleNextOrSubmit = () => {
    if (showResult) void handleNext();
    else if (selectedAnswer) handleSubmit();
    else if (isLastQuestion) void handleNext();
    else handleSkip();
  };
  const quizView = buildQuizViewModel({
    mode,
    currentIndex,
    totalQuestions: questions.length,
    answeredCount: answeredSet.size,
    isLastQuestion,
    hasSelectedAnswer: !!selectedAnswer,
    showResult,
  });

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-slate-50">
      <QuestionHeader
        examLabel={examLabel}
        sectionName={sectionDisplayName || examLabel}
        mode={mode}
        current={currentIndex + 1}
        total={questions.length}
        modeLabel={quizView.modeLabel}
        progressPercent={quizView.progressPercent}
        answeredLabel={quizView.answeredLabel}
        remainingTime={mode === 'exam' ? remainingTime : undefined}
        durationMs={mode === 'exam' ? examDurationMs : undefined}
        elapsedMs={mode === 'practice' ? practiceElapsedMs : undefined}
        clockPaused={mode === 'practice' ? practiceClockPaused : undefined}
        onToggleClockPause={
          mode === 'practice' ? handleTogglePracticeClockPause : undefined
        }
        onResetClock={mode === 'practice' ? handleResetPracticeClock : undefined}
        onExit={handleExit}
      />
      {mode === 'exam' && (
        <div className="flex justify-end gap-3 border-b border-slate-200 bg-white px-4 py-2 sm:px-6">
          <button
            type="button"
            onClick={() => setShowRestartConfirm(true)}
            className="text-sm font-medium text-slate-600 hover:text-slate-800"
          >
            重新开始
          </button>
        </div>
      )}
      <main className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-5 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
          <section className="min-w-0">
            <div className="mb-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={toggleMarkForReview}
                className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                  markedForReview.has(currentIndex)
                    ? 'border-warning-200 bg-warning-100 text-warning-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {markedForReview.has(currentIndex) ? '已标记待检查' : '标记待检查'}
              </button>
            </div>
            <div>
              {currentQuestion.passage &&
                !questionShowsAudioStimulus(currentQuestion) &&
                (currentQuestion.passageSetId || currentQuestion.passage.length > 40) && (
                  <ReadingPassageBlock
                    key={
                      currentQuestion.passageSetId ||
                      `passage:${currentQuestion.passage.slice(0, 48)}`
                    }
                    stableKey={
                      currentQuestion.passageSetId ||
                      `passage:${currentQuestion.passage.slice(0, 48)}`
                    }
                    passage={currentQuestion.passage}
                  />
                )}
              <QuestionRenderer
                key={currentQuestion.id}
                question={currentQuestion}
                questionNumber={currentIndex + 1}
                selectedAnswer={selectedAnswer}
                showResult={mode === 'practice' && showResult}
                showExplanation={showExplanation}
                mode={mode}
                suppressPassage={
                  Boolean(
                    currentQuestion.passage &&
                      !questionShowsAudioStimulus(currentQuestion) &&
                      (currentQuestion.passageSetId || currentQuestion.passage.length > 40)
                  )
                }
                onSelectAnswer={handleSelectAnswer}
                onShowExplanation={() =>
                  setShowAnalysisByQuestionId((prev) => ({
                    ...prev,
                    [currentQuestion.id]: true,
                  }))
                }
              />
            </div>
            {mode === 'practice' && selectedAnswer && (
              <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  添加笔记（仅你自己可见）
                </label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onBlur={() => {
                    if (noteText.trim()) {
                      setNote(currentQuestion.id, noteText.trim());
                    }
                  }}
                  placeholder="写下你对这道题的思考、总结或疑问..."
                  className="w-full resize-none rounded-md border border-slate-300 p-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  rows={3}
                />
                {getNote(currentQuestion.id) && (
                  <p className="mt-2 text-xs text-slate-500">已保存笔记</p>
                )}
                <div className="mt-3">
                  <QuestionFeedbackButton
                    questionId={currentQuestion.id}
                    onOpenFeedback={setFeedbackQuestionId}
                  />
                </div>
              </div>
            )}
          </section>
          <div className="lg:order-last">
            <AnswerSheet
              total={questions.length}
              currentIndex={currentIndex}
              answeredSet={answeredSet}
              markedSet={markedForReview}
              onSelectQuestion={goToQuestion}
              sections={answerSheetSections}
            />
          </div>
        </div>
      </main>
      <NavigationBar
        onPrev={handlePrev}
        onNext={showResult ? () => void handleNext() : handleNextOrSubmit}
        canPrev={currentIndex > 0}
        canNext
        isLast={isLastQuestion}
        onSubmit={() => setShowSubmitConfirm(true)}
        showSubmit
        primaryLabel={quizView.primaryActionLabel}
        submitLabel={quizView.secondaryActionLabel}
      />
      <QuizModals
        mode={mode === 'exam' ? 'exam' : 'practice'}
        showExitConfirm={showExitConfirm}
        onCancelExit={() => setShowExitConfirm(false)}
        onConfirmExit={confirmExit}
        showSubmitConfirm={showSubmitConfirm}
        onCancelSubmit={() => setShowSubmitConfirm(false)}
        onConfirmSubmit={() => {
          setShowSubmitConfirm(false);
          void handleFinishExam();
        }}
        totalQuestions={questions.length}
        answeredCount={answers.length}
        showRestartConfirm={showRestartConfirm}
        onCancelRestart={() => setShowRestartConfirm(false)}
        onConfirmRestart={handleRestartExam}
      />
      {feedbackQuestionId && (
        <QuestionFeedbackModal
          questionId={feedbackQuestionId}
          onClose={() => setFeedbackQuestionId(null)}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );
}

export default QuizPage;
